import React, { useMemo, useState } from 'react';
import { Table } from 'antd';
import { sortableContainer, sortableElement, sortableHandle } from 'react-sortable-hoc';
import { MenuOutlined } from '@ant-design/icons';
import _isArray from 'lodash/isArray';
import Pagination from '@/components/common/pagination';
import { arrayMove } from '@/common/utils';
import { SORTER } from '@/common/constant';
import { buildRowData, getColumns } from './table-settings';
import './style.scss';
import FilterView from '@/components/common/filterView';

const BaseTable = ({
  columns = [],
  data = null,
  metadata = {},
  onChangePageProps = {},
  isLoading = false,
  toggleExpandAllProp,
  rowKey = 'id',
  isPagination = true,
  isDraggable = false,
  isExpandProp = true,
  onDraggableFunc = () => {},
  onTableChangeFunc = () => {},
  scroll = null,
  isFixed = false,
  tableName = null,
  total = 0,
  filter,
  options,
  onSearch,
  showFilter = true,
  placeholder,
  containerClassName = 'content-view',
  className,
  ...args
}) => {
  const [isExpand, setExpandAll] = useState(false);

  const items = useMemo(() => {
    if (data?.length) return buildRowData(data, rowKey, tableName);
    return [];
  }, [data, metadata]);

  /**
   * Handle action click button expand & collapse
   */
  const expandAll = (isReset = false) => {
    if (!isExpandProp) return;
    const isExpandVal = isReset && typeof isReset === 'boolean' ? false : !isExpand;
    setExpandAll(isExpandVal);
    toggleExpandAllProp(isExpandVal);
  };

  /**
   * Change page for table
   * @param {*} page
   */
  const onChangePage = (page) => {
    onChangePageProps({ ...metadata, page });
    expandAll(true);
  };

  const SortableItem = sortableElement((props) => <tr {...props} />);
  const SortableContainer = sortableContainer((props) => <tbody {...props} />);
  const DragHandle = sortableHandle(() => (
    <div className="icons-container">
      <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />
    </div>
  ));

  /**
   * function used to sort data when arrange each item on UI
   * @param {*} item
   */
  const onSortEnd = (item) => {
    const { oldIndex, newIndex } = item;
    let newData = [...items];
    if (oldIndex !== newIndex) {
      newData = arrayMove([].concat(items), oldIndex, newIndex).filter((el) => !!el);
      onDraggableFunc({
        oldIndex,
        newIndex,
        orderByData: newData,
      });
    }
  };

  /**
   * Draggable Container
   * @param {*} props
   * @returns
   */
  const DraggableContainer = (props) => (
    <SortableContainer
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  /**
   * Draggable function for body row
   * @param {*} param0
   * @returns
   */
  const DraggableBodyRow = ({ className, style, ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = items.findIndex((x) => x.key === restProps['data-row-key']);
    return <SortableItem el={items[index]} index={index} {...restProps} />;
  };

  /**
   * Function will be callback when the table changes
   * @param {*} pagination
   * @param {*} filters
   * @param {*} sorter
   * @param {*} extra
   */
  const onTableChanged = (pagination, filters, sorter, extra) => {
    let sortQuery = {};
    const getSorter = ({ order, field }) => {
      const sortOrder = order === 'descend' ? SORTER.DESC : SORTER.ASC;
      return {
        sort_column: field,
        sort_order: sortOrder,
      };
    };
    sortQuery = getSorter(sorter);
    if (_isArray(sorter) && sorter.length) {
      sortQuery = sorter.map(({ field, order }) => getSorter({ field, order }));
    }

    onTableChangeFunc({
      pagination,
      filters,
      sorter,
      sortQuery,
      extra,
    });
  };

  /**
   * Get columns object
   */
  const renderColumns = getColumns(columns, (columnsP) => {
    if (isDraggable) {
      if (!columnsP.find(({ dataIndex }) => dataIndex === 'sort')) {
        columnsP.unshift({
          title: '',
          dataIndex: 'sort',
          width: '50px',
          fixed: 'left',
          render: (_val, { isChildren = false }) => !isChildren && <DragHandle />,
        });
      }
    }
    return [...columnsP];
  });

  const getFixedScrollColumn = () => {
    const scale = { x: 1200 };
    let isAllowScroll = false;
    const getWidth = window.screen.width;
    if ((getWidth <= 1440 && renderColumns.length > 6) || getWidth < 1024) {
      isAllowScroll = true;
    }
    return isAllowScroll && isFixed ? scale : {};
  };

  return (
    <>
      <div className={`base-table-card ${containerClassName}`}>
        {showFilter && (
          <FilterView
            expandAll={isExpand}
            filter={filter}
            metadata={metadata}
            onChangePageProps={onChangePageProps}
            toggleExpandAllProp={toggleExpandAllProp}
            isPagination={isPagination}
            onSearch={onSearch}
            isExpandProp={isExpandProp}
            options={options}
            placeholder={placeholder}
          />
        )}
        {data !== null && (
          <Table
            rowKey={rowKey}
            columns={renderColumns}
            pagination={false}
            dataSource={items}
            className={className}
            loading={isLoading}
            components={
              isDraggable && { body: { wrapper: DraggableContainer, row: DraggableBodyRow } }
            }
            onChange={onTableChanged}
            scroll={scroll || getFixedScrollColumn()}
            {...args}
          />
        )}
      </div>

      {data !== null && isPagination && items.length >= 0 && (
        <Pagination
          total={total}
          pageSize={metadata.limit}
          current={metadata.page}
          onChange={onChangePage}
        />
      )}
    </>
  );
};
const MemoBaseTable = React.memo(BaseTable);
export default MemoBaseTable;
